import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  CheckoutModalOpenOptions,
  SubscriptionTierType,
  CheckoutProps,
} from '@picsart/monetization/types';
import { CheckoutModalProvider } from '@picsart/monetization/CheckoutModalContext';
import { TIERS } from '@picsart/monetization/constants';
import { authStatus, useAuthContext } from '@picsart/growth-rc/AuthContext';
import { getStorage } from '@picsart/growth-rc/services/browserStorage';
import CheckoutModal from '@picsart/monetization/CheckoutModal';

import useScreenName from 'hooks/useScreenName';

import useMonetizationDataStore from 'stores/monetizationDataStore';
import usePageDataStore from 'stores/pageDataStore';

import { CTA_TEXT, adyenKey, getWithRedirectionPL } from 'constants/main';

import { getSubscriptionPlan } from 'services/Subscription';

import type MonetizationPopupTypes from './types';

import ReddemErrorModal from './ReddemErrorModal';

import useCustomFlows from './useCustomFlows';

const openEditor = () => {
  window.location.href = '/create';
};

// This must be replaced old logics which is very hard to understand current and add upcoming features
// Now it only contains popup from  header (autoSave subscription feature), but we must move rest flows to here.

function MonetizationPopup({ promoRequired, scope, children }: MonetizationPopupTypes) {
  const paymentDone = useRef<boolean>();
  const actionButtonValue = getStorage(CTA_TEXT);
  const screenName = useScreenName();
  const { errMessage, isErrorModalOpen, setIsErrorModalOpen, modalView, isCustomWelcomePopup } =
    useCustomFlows(promoRequired);

  const monetizationPopupTrigger = useMonetizationDataStore(x => x.monetizationPopupTrigger);
  const openMonetizationPopup = useMonetizationDataStore(x => x.openMonetizationPopup);
  const planData = useMonetizationDataStore(x => x.planData);
  const setPlanData = useMonetizationDataStore(x => x.setPlanData);
  const onPaymentSuccess = useMonetizationDataStore(x => x.onPaymentSuccess);
  const isCloseDisabled = useMonetizationDataStore(x => x.isCloseDisabled);
  const monetizationData = useMonetizationDataStore(x => ({
    enableTiers: x.enableTiers,
    forceTier: x.forceTier,
    isLoading: x.tiersDataIsLoading,
    isTeamDisabled: x.isTeamDisabled,
  }));
  const checkoutOptions = useMonetizationDataStore(x => x.checkoutOptions);
  const setCheckoutOptions = useMonetizationDataStore(x => x.setCheckoutOptions);
  const { cmsEnableTiers, cmsForceTier, isMaximizerMode } = usePageDataStore(state => ({
    cmsEnableTiers: state?.pageData?.data?.monetizationEnableTiers,
    cmsForceTier: state?.pageData?.data?.monetizationForceTier,
    isMaximizerMode: state?.pageData?.data?.isMaximizerMode,
  }));
  const { user, onUserChange, status: userStatus } = useAuthContext();
  const [searchParams] = useSearchParams();
  const promoVal: string = decodeURIComponent(searchParams.get('promo') || '');

  const isTMobile = screenName === 't_mobile-trial';
  const isColgate = screenName === 'colgate_trial';
  const isPro = scope?.includes('_pro');
  const enableTiers =
    !!(isTMobile || isPro || isColgate) || monetizationData.enableTiers || cmsEnableTiers;
  const forcePro: SubscriptionTierType = isTMobile || isPro || isColgate ? 'pro' : null;

  const gtmData = useMemo(
    () => ({
      screen_id: `landing_${screenName}`,
      source: screenName ? `landing_${screenName.replace(' ', '_').toLowerCase()}` : '',
      action_button: actionButtonValue,
    }),
    [screenName, actionButtonValue],
  );

  // It's only for Galaxy Z Flip and Galaxy Z Fold users. Should only work on these mobile devices.
  // For more information --> https://picsart.atlassian.net/wiki/spaces/PWEB/pages/2500363662/Samsung+Z+Partnership

  // get data for subscription and
  const subscriptionsServiceData = useCallback(async () => {
    const data = await getSubscriptionPlan(scope);
    if (data.length) {
      setPlanData(data);
    }
  }, [scope, setPlanData]);

  useEffect(() => {
    if (
      (userStatus === authStatus.failed || userStatus === authStatus.resolved) &&
      !planData?.length
    ) {
      void subscriptionsServiceData();
    }
  }, [planData?.length, subscriptionsServiceData, user, userStatus]);
  const handlePaymentSuccess = useCallback(() => {
    onPaymentSuccess?.();
    paymentDone.current = true;
  }, [onPaymentSuccess]);

  const closePopup = useCallback(() => {
    const redirectionUrl = getWithRedirectionPL(screenName);
    setCheckoutOptions(null);
    // get redirection url for partnership page
    if ((user?.subscribed || user?.subscription.granted) && redirectionUrl) {
      // It is for auth popup which coming from monetization
      window.location.href = redirectionUrl;
      return false;
    }
    return monetizationPopupTrigger(false);
  }, [monetizationPopupTrigger, screenName, user, setCheckoutOptions]);

  const popupOpennerTrigger = useCallback(
    (isOpen: boolean, props: CheckoutModalOpenOptions) => {
      setCheckoutOptions(props);
      monetizationPopupTrigger(isOpen);
      if (!isOpen && paymentDone.current && !user?.subscribed) {
        onUserChange({
          ...user,
          subscribed: true,
        });
      }
    },
    [monetizationPopupTrigger, onUserChange, user, setCheckoutOptions],
  );

  const areNewTierPlansProvided = TIERS.includes(planData?.[0]?.tier_id);
  const areTiersEnabled = enableTiers || areNewTierPlansProvided;

  const subscriptionPlans = useMemo(() => {
    if (
      scope === 'custom_API_flow' ||
      planData?.length === 0 ||
      (enableTiers && !areNewTierPlansProvided)
    ) {
      return undefined;
    }
    return planData;
  }, [scope, planData, enableTiers, areNewTierPlansProvided]);

  const checkoutProps = useMemo<CheckoutProps>(
    () => ({
      callPaymentSuccessOn3dSecure: true,
      onPaymentSuccess: handlePaymentSuccess,
      onCompletePaymentClick: openEditor,
      isPromoRequired: promoRequired,
      subscriptionPlans,
      initialView: modalView,
      forceTier: monetizationData.forceTier || forcePro || cmsForceTier,
      areTiersEnabled,
      isMaximizerMode,
      adyenKey,
      gtmData,
      promoCode: promoVal,
      isTeamDisabled: monetizationData.isTeamDisabled,
      ...(isCustomWelcomePopup
        ? {
            welcomeView: 'mobile',
            storeUrl: 'https://picsart.onelink.me/XqUB/l0v4c5j4',
          }
        : {}),
      ...checkoutOptions,
    }),
    [
      checkoutOptions,
      handlePaymentSuccess,
      subscriptionPlans,
      modalView,
      promoRequired,
      forcePro,
      promoVal,
      gtmData,
      isCustomWelcomePopup,
      monetizationData,
      cmsForceTier,
      areTiersEnabled,
    ],
  );

  return (
    <CheckoutModalProvider setIsOpen={popupOpennerTrigger} isOpen={openMonetizationPopup}>
      {children}
      {(userStatus === authStatus.failed || userStatus === authStatus.resolved) &&
        !isErrorModalOpen &&
        !monetizationData.isLoading && (
          <CheckoutModal
            onAlreadySubscribed={closePopup}
            checkoutProps={checkoutProps}
            isCloseDisabled={isCloseDisabled}
          />
        )}
      {isErrorModalOpen && (
        <ReddemErrorModal setIsModalOpen={setIsErrorModalOpen} errorMessage={errMessage} />
      )}
    </CheckoutModalProvider>
  );
}

export default MonetizationPopup;
